/* eslint-disable ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/require-tagless-components */
import Component from '@ember/component';
import { action, computed } from '@ember/object';
import { mapBy } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import AssessmentDisplay from '@linc-technologies/linced-common/enums/assessment-display';
import MediaType from '@linc-technologies/linced-common/enums/media-type';
import Options from '@linc-technologies/linced-common/enums/options';
import {
  getTagCountsForPage,
  getTagPieData,
} from '@linc-technologies/linced-common/utils/tags';
import { classNames } from '@ember-decorators/component';
import moment from 'moment';

@classNames('page')
export default class PrintPage extends Component {
  @service
  dates;

  @service
  options;

  @service
  permissions;

  MediaType = MediaType;

  // mode is the report-printer's mode.
  @tracked mode = 'report';

  // page is the Page to be printed.
  page = null;

  // includePageTitle specifies whether to display the Page title.
  includePageTitle = false;

  // endTime is a timestamp, to be fed into any components which fetch/filter
  // their own data.
  endTime = null;

  // pageBlocks specifies the order in which the content blocks should appear.
  pageBlocks = Object.freeze(['assessments', 'subjects', 'posts']);

  // pageDescription is the text content that has been included in the page
  // description
  pageDescription = null;

  // pageMedia is any media that has been included in the page description
  pageMedia = null;

  // posts is the list of posts to be displayed for this Page.
  posts = null;

  // filterFrom enables filtering posts by date.
  filterFrom = '';

  // filterTo enables filtering posts by date.
  filterTo = '';

  // filterTags enables filtering posts by tag.
  filterTags = [];

  // isParentView specifies whether to render the Page in parent view.
  isParentView = false;

  // includeDescription specifies whether to show the Page description.
  includeDescription = true;

  // includeDescriptionImage specifies whether to show the Page description.
  includeDescriptionImage = true;

  // includeAuthorImages specifies whether to show authors' profile images.
  includeAuthorImages = true;

  // includeImages specifies whether to show images attached to Posts.
  includeImages = true;

  // includeOtherMedia specifies whether to list other media attached to Posts.
  includeOtherMedia = false;

  // includeLegalName specifies whether to include the students legal name.
  includeLegalName = false;

  // includeComments specifies whether to show comments on Posts.
  includeComments = false;

  // includeTagsOverview specifies whether to include a Tags Overview.
  includeTagsOverview = false;

  // includeAssessmentOverview specifies whether to include assessment overview on pages
  @tracked includeAssessmentOverview = false;

  // includeAssessmentOverviewOnHomepage specifies whether to include assessment overview the report page
  @tracked includeAssessmentOverviewOnHomepage = false;

  // goalSubjects is a list of goal subjects which are accessible to the user,
  // to avoid issues for users (i.e. caregivers) who don't have access to all.
  goalSubjects = null;

  // developingStates is a list of recent Goal States for in-progress goals.
  developingStates = null;

  // secureStates is a list of Goal States for recently completed goals.
  secureStates = null;

  // levels specifies the Progress Levels to use to generate the report.
  levels = null;

  // expectations is a map of subject ID to array of Expectations.
  expectations = null;

  // progressions is a map of subject ID to array of Progressions.
  progressions = null;

  // learnerSummaries is a map of subject ID to learner subject summary.
  learnerSummaries = null;

  // grades specifies the grades to be included in the report.
  grades = null;

  // timeframes specifies the timeframes for the reports grade overviews
  timeframes = null;

  // terms specifies the terms for the reports grade overviews
  terms = null;

  // numberOfAssessmentTerms specifies how many terms to show grades for.
  numberOfAssessmentTerms = 4;

  // sortedTerms returns terms sorted by year and term limited to the
  // configured amount of terms to show.
  sortedTerms(gradeColumns) {
    let sortedTerms = this.terms
      .slice()
      .sort((a, b) => (a.start || '').localeCompare(b.start || ''));
    let currentTerm = this.terms.find((term) => term.isCurrent);
    if (!isEmpty(currentTerm)) {
      let index = sortedTerms.indexOf(currentTerm);
      let minIndex = Math.max(0, index - gradeColumns + 1);
      return sortedTerms.slice(minIndex, index + 1);
    }

    return sortedTerms;
  }

  // configuredTimeframes gets timeframes limited to the
  // configured amount of columns to show.
  configuredTimeframes(gradeColumns) {
    let index = 0;
    let minIndex = 0;
    let currentTimeframe = this.timeframes.find(
      (timeframe) => timeframe.isCurrent,
    );
    let closestTimeframe = this.dates.findClosestTimeframe(this.timeframes);
    if (!isEmpty(currentTimeframe)) {
      index = this.timeframes.indexOf(currentTimeframe);
      minIndex = Math.max(0, index - gradeColumns + 1);
    } else {
      // we're in between timeframes, return the closest one and beyond
      index = this.timeframes.indexOf(closestTimeframe);
      minIndex = Math.max(0, index - gradeColumns + 1);
    }
    return this.timeframes.slice(minIndex, index + 1);
  }

  getAlternateCutOffDate(gradeColumns) {
    // return term cut off date if no timeframes
    if (isEmpty(this.timeframes)) {
      return this.getTermCutOffDate(gradeColumns);
    }
    return this.configuredTimeframes(gradeColumns).reverse().at(-1)
      .startDateUnix;
  }

  monthCutOffDate(gradeColumns) {
    let today = moment();
    let finalMonth = moment(today)
      .subtract(gradeColumns, 'months')
      .startOf('month');
    return moment(finalMonth).endOf('month').unix();
  }

  getTermCutOffDate(gradeColumns) {
    return moment(
      this.sortedTerms(gradeColumns).reverse().at(-1).start,
      'YYYY-MM-DD',
    ).unix();
  }

  // Intermediary CP so the @each dependent key below stays one level deep
  // (Ember 4 forbids two-level keys like grades.@each.assessment.foo).
  @mapBy('grades', 'assessment')
  gradeAssessments;

  @computed(
    'grades.@each.createTime',
    'gradeAssessments.@each.{assessmentDisplay,noGradesColumnsToShowCaregiversStudents}',
    'alternateCutOffDate',
  )
  get filteredGrades() {
    return this.grades.filter((grade) => {
      let gradeColumns = 4;
      if (grade.get('assessment.noGradesColumnsToShowCaregiversStudents')) {
        gradeColumns = grade.get(
          'assessment.noGradesColumnsToShowCaregiversStudents',
        );
      }
      switch (grade.get('assessment.assessmentDisplay')) {
        case AssessmentDisplay.ALTERNATE:
          return grade.createTime > this.getAlternateCutOffDate(gradeColumns);
        case AssessmentDisplay.MONTHS:
          return grade.createTime > this.monthCutOffDate(gradeColumns);
        default:
          return grade.createTime > this.getTermCutOffDate(gradeColumns);
      }
    });
  }

  @computed('posts.@each.assessment_id')
  get unlinkedPosts() {
    return this.posts.filter((post) => !post.assessment_id);
  }

  @computed('school')
  get numPostsPerPage() {
    return this.options.schoolGet(
      this.school,
      Options.REPORTING_REPORT_POSTS_PER_PAGE,
    );
  }

  get showAssessmentOverview() {
    if (this.mode === 'page') {
      return this.includeAssessmentOverview;
    }
    return this.includeAssessmentOverviewOnHomepage;
  }

  @computed('page.tagCategories.[]', 'posts.@each.tags')
  get tagCounts() {
    return getTagCountsForPage(this.page, this.posts);
  }

  @computed('numPostsPerPage', 'mode', 'unlinkedPosts')
  get filteredUnlinkedPosts() {
    if (this.mode === 'report' && this.numPostsPerPage !== 'all') {
      return this.unlinkedPosts.slice(0, this.numPostsPerPage);
    }
    return this.unlinkedPosts;
  }

  @action
  getTagPieData() {
    return getTagPieData.call(this, ...arguments);
  }

  @action
  filterAssessmentPosts(assessmentPosts) {
    if (this.mode === 'report' && this.numPostsPerPage !== 'all') {
      return assessmentPosts.slice(0, this.numPostsPerPage);
    }
    return assessmentPosts;
  }
}
